import axios from "axios";
import { Skeleton, Tooltip } from "@chakra-ui/react";
import moment from "moment";
import { useEffect, useState } from "react";
import { NotificationManager } from "react-notifications";
import { useSelector } from "react-redux";
import { Link, useLocation, useNavigate } from "react-router-dom";
import Plus from "../../assets/svgs/Plus.svg";
import addCompanyIcon from "../../assets/svgs/add-company.svg";
import sortAsce from "../../assets/svgs/sort-ascending.svg";
import sortDesc from "../../assets/svgs/sort-descending.svg";
import swap from "../../assets/svgs/swap-vertical.svg";
import ColumnCustomizer from "../../components/ColumnCustomizer";
import ImportExport from "../../components/ImportExport";
import Loader from "../../components/Loader";
import DataAsOfNotice from "../../components/DataAsOfNotice";
import { ROUTES } from "../../routes/routeKeys";
import ContactPagination from "../../components/Pagination/ContactPagination";
import useAuth from "../../hooks/useAuth";
import BaseLayout from "../../layouts/BaseLayout";
import { BASE_URL } from "../../utils/Element";
import { initialCompanyFilterData } from "../../utils/initialData";
import { formatPhoneNumber } from "../../utils/utils";
import AddCompanyModal from "./AddCompanyModal";
import "./Companies.css";
import AdvanceCompanyFilter from "./components/AdvanceCompanyFilter";
import { FiSettings } from "react-icons/fi";


// Custom debounce hook
const useDebounce = (value, delay = 600) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => clearTimeout(handler);
  }, [value, delay]);

  return debouncedValue;
};

const Companies = () => {
  const [config] = useAuth();
  const navigate = useNavigate();
  const [tagss, setTagss] = useState([]);
  const [loading, setLoading] = useState(true);
  const location = useLocation();
  const [showModal, setShowModal] = useState(false);
  const [showColumnModal, setShowColumnModal] = useState(false); // ← New State

  const [currentPage, setCurrentPage] = useState(1);
  const [itemPerPage, setItemPerPage] = useState(50);
  const [selectedItem, setSelectedItem] = useState([]);
  const [isSelectAll, setIsSelectAll] = useState(false);
  const [companiesData, setCompaniesData] = useState([]);
  const [sortBy, setSortBy] = useState("updated_at");
  const [sortDirection, setSortDirection] = useState("desc");
  const isSidebarCollapsed = useSelector((state) => state.isSidebarCollapsed);
  const [paginationData, setPaginationData] = useState({ totalItems: 0, from: 0, to: 0, totalPages: 0 });

  const [filterData, setFilterData] = useState(() => {
    const localFilterData = localStorage.getItem("filterData");
    return localFilterData ? JSON.parse(localFilterData) : initialCompanyFilterData;
  });

  const [advanceFilterData, setAdvanceFilterData] = useState(() => {
    const localFilterData = localStorage.getItem("filterData");
    return localFilterData ? JSON.parse(localFilterData) : [];
  });

  // ==================== COLUMN CUSTOMIZATION ====================
  const allColumns = [
    { key: 'select', label: 'Select', fixed: true },
    { key: 'company_name', label: 'Company Name', fixed: true },
    { key: 'last_contacted', label: 'Last Contacted' },
    { key: 'website_url', label: 'Website Url' },
    { key: 'street', label: 'Street' },
    { key: 'city', label: 'City' },
    { key: 'state', label: 'State' },
    { key: 'company_phone', label: 'Company Phone' },
    { key: 'tags', label: 'Tags' },
  ];

  const [visibleColumns, setVisibleColumns] = useState(() => {
    const saved = localStorage.getItem('companyVisibleColumns');
    return saved
      ? JSON.parse(saved)
      : allColumns.map(col => col.key); // Show all by default
  });
  // ============================================================

  const queryParams = new URLSearchParams(location.search);

  // Search states
  const [searchInput, setSearchInput] = useState(queryParams.get('search') ?? '');
  const debouncedSearch = useDebounce(searchInput, 600);
  const [search, setSearch] = useState(queryParams.get('search') ?? '');

  const companyAllIds = companiesData?.map((el) => el.id);

  const buildQueryParams = (filters) => {
    let params = `page=${currentPage}&per_page=${itemPerPage}`;

    if (search) {
      const encodedSearch = encodeURIComponent(search);
      params += `&search=${encodedSearch}`;
    }

    const { company_name, property, primaryphone, owner_without_tags, owner_tags, streetaddress, city, state, zip, website, count, start_count, end_count, taxrecord, taxrecordsentdate, taxrecordsentSdate, taxrecordsentEdate, tags = [], last_contact, lastContactSentDate, lastContactSentEdate, lastContactSentSdate, date_category, longer_than_day, within_last_day } = filters;

    if (company_name !== undefined) params += `&company_name=${encodeURIComponent(company_name)}`;
    if (property !== "" && property !== undefined) params += `&property=${encodeURIComponent(property)}`;
    if (primaryphone !== undefined) params += `&phone=${encodeURIComponent(primaryphone)}`;
    if (streetaddress !== undefined) params += `&street_address=${encodeURIComponent(streetaddress)}`;
    if (city !== undefined) params += `&city=${encodeURIComponent(city)}`;
    if (state !== undefined) params += `&state=${encodeURIComponent(state)}`;
    if (zip !== undefined) params += `&zip_code=${encodeURIComponent(zip)}`;
    if (website !== undefined) params += `&website=${encodeURIComponent(website)}`;
    if (taxrecord !== undefined) params += `&tax_record_sent=${encodeURIComponent(taxrecord)}`;
    if (taxrecordsentdate !== undefined) params += `&tax_record_sent_date=${encodeURIComponent(taxrecordsentdate)}`;
    if (taxrecordsentSdate !== undefined) params += `&tax_record_start_date=${encodeURIComponent(taxrecordsentSdate)}`;
    if (taxrecordsentEdate !== undefined) params += `&tax_record_end_date=${encodeURIComponent(taxrecordsentEdate)}`;
    if (tags.length > 0) params += `&tags=${tags.join(",")}`;
    if (last_contact !== undefined) params += `&last_contact=${encodeURIComponent(last_contact)}`;
    if (lastContactSentDate !== undefined) params += `&last_update_date=${encodeURIComponent(lastContactSentDate)}`;
    if (lastContactSentSdate !== undefined) params += `&last_update_start_date=${encodeURIComponent(lastContactSentSdate)}`;
    if (lastContactSentEdate !== undefined) params += `&last_update_end_date=${encodeURIComponent(lastContactSentEdate)}`;
    if (date_category !== undefined) params += `&date_category=${encodeURIComponent(date_category)}`;
    if (within_last_day) params += `&within_last_day=${encodeURIComponent(within_last_day)}`;
    if (longer_than_day) params += `&longer_than_day=${encodeURIComponent(longer_than_day)}`;
    if (count !== undefined) params += `&count=${encodeURIComponent(count)}`;
    if (start_count !== undefined) params += `&start_count=${encodeURIComponent(start_count)}`;
    if (end_count !== undefined) params += `&end_count=${encodeURIComponent(end_count)}`;
    if (sortDirection !== undefined && sortBy !== "id") params += `&sort_direction=${sortDirection}`;
    if (sortBy !== "id" && sortBy !== undefined) params += `&sort_by=${sortBy}`;
    if (owner_tags?.length > 0) params += `&owner_tags=${encodeURIComponent(owner_tags)}`;
    if (owner_without_tags?.length > 0) params += `&owner_without_tags=${encodeURIComponent(owner_without_tags)}`;

    localStorage.setItem("queryParams", params);
    return params;
  };

  const handleSelectCheck = (item) => {
    const isSelected = selectedItem.some((el) => el.id === item.id);
    if (isSelected) {
      setSelectedItem(selectedItem.filter((el) => el.id !== item.id));
    } else {
      setSelectedItem([...selectedItem, item]);
    }
  };

  const fetchCompanies = (filters, advanceFilters = []) => {
    const queryParams = buildQueryParams(filters);
    const validAdvanceFilters = advanceFilters.filter(group =>
      group.filters && group.filters.some(f => f.field && f.operator)
    );
    const advanceFiltersQuery = encodeURIComponent(JSON.stringify(validAdvanceFilters));

    setLoading(true);
    axios
      .get(`${BASE_URL}/company-list?${queryParams}&advance_filters=${advanceFiltersQuery}`, config)
      .then((res) => {
        const value = res?.data?.data;
        setCompaniesData(value?.companies?.data || []);
        setPaginationData({
          totalItems: value?.companies?.total ?? 0,
          from: value?.companies?.from ?? 0,
          to: value?.companies?.to ?? 0,
          totalPages: value?.companies?.last_page ?? 0,
        });
        setSelectedItem([]);
        setIsSelectAll(false);
      })
      .catch((err) => {
        if (err.response?.data?.message && err.response?.data?.message !== "Too Many Attempts.") {
          NotificationManager.error(err.response?.data?.message);
        }
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchCompanies(filterData, advanceFilterData);
  }, [sortBy, sortDirection]);

  useEffect(() => {
    onSuccess(filterData, advanceFilterData);
  }, [currentPage, itemPerPage, search]);

  const handleNext = () => {
    if (currentPage !== paginationData.totalPages) setCurrentPage(currentPage + 1);
  };

  const handlePrev = () => {
    if (currentPage > 1) setCurrentPage(currentPage - 1);
  };

  const onSuccess = (filters, advanceFilters) => {
    setLoading(true);
    const normalFilters = filters || filterData;
    const advFilters = advanceFilters !== undefined ? advanceFilters : advanceFilterData;
    fetchCompanies(normalFilters, advFilters);
  };

  // Debounce & URL sync
  useEffect(() => {
    if (debouncedSearch.length >= 3 || debouncedSearch === '') {
      setSearch(debouncedSearch);
    }
  }, [debouncedSearch]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (search.trim()) params.set('search', search);
    else params.delete('search');
    navigate({ search: params.toString() }, { replace: true });
  }, [search, navigate, location.search]);

  const handleInputChange = (e) => {
    const newValue = e.target.value;
    const trimmed = newValue.startsWith(" ") ? newValue.trimStart() : newValue;
    setSearchInput(trimmed);
  };

  const selectedCompanies = selectedItem.length !== 0 ? companiesData?.filter((el) => selectedItem?.includes(el?.id)) : [];

  return (
    <BaseLayout>
      <div className="">
        <div className="contact-header">
          <p className="head-1 dark-H">
            Companies
            {selectedItem?.length > 0 && (
              <span className="body-L ml-2">
                ({selectedItem?.length} <span className="dark-H">{selectedItem?.length === 1 ? "item" : "items"} selected</span>)
              </span>
            )}
          </p>

        </div>

        <div className="md:flex justify-between items-center mb-4 mt-2">
          <div className="flex gap-4 items-center flex-wrap">
            <AdvanceCompanyFilter
              filterData={advanceFilterData}
              onSetFilterData={setAdvanceFilterData}
              onCallApiAgain={(newAdvanceFilters) => {
                setAdvanceFilterData(newAdvanceFilters);
                fetchCompanies(filterData, newAdvanceFilters);
              }}
            />

            <ImportExport
              from="company"
              onCallApi={() => {
                onSuccess(filterData, advanceFilterData);
                setSelectedItem([]);
              }}
              filterData={filterData}
              advanceFilterData={advanceFilterData}
            />

            <div className="search-box contacts relative w-[250px]">
              <input
                type="text"
                className="body-S"
                placeholder="Search Name and Website Link"
                value={searchInput}
                onChange={handleInputChange}
                style={{ border: "1px solid #D8D8D8", marginTop: "0px", width: "100%" }}
              />
            </div>
          </div>

          <div className="flex gap-2 items-center">
            {/* Customize Columns Button */}
            <button
              className="add-contact-button green-bg-H light-L body-S flex items-center gap-1"
              onClick={() => setShowColumnModal(true)}
            >
              <FiSettings className="w-4 h-4" /> Columns 
            </button>

            <button
              className="add-contact-button green-bg-H light-L body-S"
              onClick={() => setShowModal(true)}
            >
              <img className="mr-2 sidebar-icons" src={Plus} alt="plus" /> Add
            </button>
          </div>
        </div>
      </div>

      <div className="light-bg-L pb-3 table-container bg-white rounded-xl border border-gray-200 overflow-hidden relative min-h-[400px]">
        {loading && (
          <div className="absolute inset-0 z-10 flex justify-center items-center bg-white/40 backdrop-blur-[1px]">
            <Loader />
          </div>
        )}
        <div className={`${isSidebarCollapsed ? "collapsed-width" : "width"} table-info light-bg-L`}>
          <table className="contact-table two-rows-static light-bg-L">
              <thead>
                <tr className="uppercase body-N dark-M">
                  {visibleColumns.includes('select') && (
                    <th>
                      <label className="container">
                        <input
                          type="checkbox"
                          checked={selectedItem.length > 0 && selectedItem.length === companiesData.length}
                          onChange={(e) => {
                            const isChecked = e.target.checked;
                            setIsSelectAll(isChecked);
                            if (isChecked) setSelectedItem([...companiesData]);
                            else setSelectedItem([]);
                          }}
                        />
                        <span className="checkmark"></span>
                      </label>
                    </th>
                  )}

                  {visibleColumns.includes('company_name') && (
                    <th>
                      <div className="table-header green-H">
                        Company Name
                        <img role="button" src={sortBy !== "company_name" ? swap : sortDirection === "desc" ? sortDesc : sortAsce}
                          onClick={() => { setSortBy("company_name"); setSortDirection(sortDirection === "desc" ? "asc" : "desc"); }} alt="icon" />
                      </div>
                    </th>
                  )}

                  {visibleColumns.includes('last_contacted') && (
                    <th>
                      <div className="table-header">
                        Last Contacted
                        <img role="button" src={sortBy !== "updated_at" ? swap : sortDirection === "desc" ? sortDesc : sortAsce}
                          onClick={() => { setSortBy("updated_at"); setSortDirection(sortDirection === "desc" ? "asc" : "desc"); }} alt="icon" />
                      </div>
                    </th>
                  )}

                  {visibleColumns.includes('website_url') && (
                    <th>
                      <div className="table-header">
                        Website Url
                        <img role="button" src={sortBy !== "website_link" ? swap : sortDirection === "desc" ? sortDesc : sortAsce}
                          onClick={() => { setSortBy("website_link"); setSortDirection(sortDirection === "desc" ? "asc" : "desc"); }} alt="icon" />
                      </div>
                    </th>
                  )}

                  {visibleColumns.includes('street') && (
                    <th>
                      <div className="table-header">Street
                        <img role="button" src={sortBy !== "street" ? swap : sortDirection === "desc" ? sortDesc : sortAsce}
                          onClick={() => { setSortBy("street"); setSortDirection(sortDirection === "desc" ? "asc" : "desc"); }} alt="icon" />
                      </div>
                    </th>
                  )}

                  {visibleColumns.includes('city') && (
                    <th>
                      <div className="table-header">City
                        <img role="button" src={sortBy !== "city" ? swap : sortDirection === "desc" ? sortDesc : sortAsce}
                          onClick={() => { setSortBy("city"); setSortDirection(sortDirection === "desc" ? "asc" : "desc"); }} alt="icon" />
                      </div>
                    </th>
                  )}

                  {visibleColumns.includes('state') && (
                    <th>
                      <div className="table-header">State
                        <img role="button" src={sortBy !== "state" ? swap : sortDirection === "desc" ? sortDesc : sortAsce}
                          onClick={() => { setSortBy("state"); setSortDirection(sortDirection === "desc" ? "asc" : "desc"); }} alt="icon" />
                      </div>
                    </th>
                  )}

                  {visibleColumns.includes('company_phone') && (
                    <th>
                      <div className="table-header">Company Phone
                        <img role="button" src={sortBy !== "phone" ? swap : sortDirection === "desc" ? sortDesc : sortAsce}
                          onClick={() => { setSortBy("phone"); setSortDirection(sortDirection === "desc" ? "asc" : "desc"); }} alt="icon" />
                      </div>
                    </th>
                  )}

                  {visibleColumns.includes('tags') && (
                    <th>
                      <div className="table-header">Tags
                        <img role="button" src={sortBy !== "communication" ? swap : sortDirection === "desc" ? sortDesc : sortAsce}
                          onClick={() => { setSortBy("communication"); setSortDirection(sortDirection === "desc" ? "asc" : "desc"); }} alt="icon" />
                      </div>
                    </th>
                  )}
                </tr>
              </thead>

              <tbody>
                {loading ? (
                  [...Array(10)].map((_, idx) => (
                    <tr key={idx} className="dark-H body-N">
                      {visibleColumns.includes('select') && <td><Skeleton height="20px" width="20px" /></td>}
                      {visibleColumns.includes('company_name') && <td><Skeleton height="20px" width="140px" /></td>}
                      {visibleColumns.includes('last_contacted') && <td><Skeleton height="20px" width="100px" /></td>}
                      {visibleColumns.includes('website_url') && <td><Skeleton height="20px" width="120px" /></td>}
                      {visibleColumns.includes('street') && <td><Skeleton height="20px" width="120px" /></td>}
                      {visibleColumns.includes('city') && <td><Skeleton height="20px" width="100px" /></td>}
                      {visibleColumns.includes('state') && <td><Skeleton height="20px" width="80px" /></td>}
                      {visibleColumns.includes('company_phone') && <td><Skeleton height="20px" width="120px" /></td>}
                      {visibleColumns.includes('tags') && <td><Skeleton height="20px" width="80px" /></td>}
                    </tr>
                  ))
                ) : companiesData.length === 0 ? (
                  <tr style={{ border: "none" }} className="border-none">
                    <td colSpan={visibleColumns.length} className="body-N text-center mt-6 p-10 border-none" style={{ border: "none" }}>
                      <div className="flex flex-col items-center justify-center py-12">
                        <img src={addCompanyIcon} alt="No Companies" className="w-16 h-16 mb-4 opacity-60" />
                        <p className="head-4 dark-H mb-2">No Companies Found</p>
                        <p className="body-N dark-M mb-6">There are no companies matching your search or filters.</p>
                        <button
                          className="add-contact-button green-bg-H light-L body-S flex items-center gap-2"
                          onClick={() => setShowModal(true)}
                        >
                          <img className="sidebar-icons" src={Plus} alt="plus" /> Add Company
                        </button>
                      </div>
                    </td>
                  </tr>
                ) : (
                  companiesData.flatMap((el, idx) => (
                    <tr key={idx} className="dark-H body-N">
                    {visibleColumns.includes('select') && (
                      <td>
                        <label className="container">
                          <input
                            type="checkbox"
                            checked={selectedItem.some((item) => item.id === el.id)}
                            onChange={() => handleSelectCheck(el)}
                          />
                          <span className="checkmark"></span>
                        </label>
                      </td>
                    )}

                    {visibleColumns.includes('company_name') && (() => {
                      const companyName = el.company_name || "";
                      const displayCompany = companyName.length > 25 ? companyName.substring(0, 25) + "..." : companyName;
                      return (
                        <td className="head-5 normal-case">
                          <Tooltip label={companyName} bg="white" color="black" fontWeight="normal" hasArrow placement="top" isDisabled={companyName.length <= 25}>
                            <Link to={`${ROUTES.COMPANY_DETAILS.replace(":id", el.id)}?search=${search}`} onClick={(e) => { e.preventDefault(); navigate(`${ROUTES.COMPANY_DETAILS.replace(":id", el.id)}?search=${search}`); }}>
                              {displayCompany}
                            </Link>
                          </Tooltip>
                        </td>
                      );
                    })()}

                    {visibleColumns.includes('last_contacted') && (
                      <td>
                        {el?.latest_activity_including_contacts?.date
                          ? moment(el.latest_activity_including_contacts.date).format("MM/DD/YY")
                          : ''}
                        <span className="dark-M body-S">
                          {el?.latest_activity_including_contacts?.date_connected
                            ? ` (${moment(el.latest_activity_including_contacts.date_connected).fromNow()})`
                            : ''}
                        </span>
                      </td>
                    )}

                    {visibleColumns.includes('website_url') && (
                      <td
                        role={el.website_link ? "button" : ""}
                        className="green-H"
                        onClick={() => {
                          if (el.website_link) {
                            const validLink = el.website_link.startsWith('http') ? el.website_link : `http://${el.website_link}`;
                            window.open(validLink, "_blank");
                          }
                        }}
                      >
                        {el.website_link?.slice(0, 35)}
                      </td>
                    )}

                    {visibleColumns.includes('street') && (
                      <td>{el.address && el.address.length > 0 && el.address[0].street}</td>
                    )}

                    {visibleColumns.includes('city') && (
                      <td>{el.address && el.address.length > 0 && el.address[0].city}</td>
                    )}

                    {visibleColumns.includes('state') && (
                      <td>{el.address && el.address.length > 0 && el.address[0].state}</td>
                    )}

                    {visibleColumns.includes('company_phone') && (
                      <td>{formatPhoneNumber(el.phone?.[0]?.phone_number)}</td>
                    )}

                    {visibleColumns.includes('tags') && (
                      <td className="flex items-center gap-2">
                        {el?.communication?.map((tag, i) =>
                          tag ? (
                            <p key={i} className="tags green-H body-S">{tag}</p>
                          ) : null
                        )}
                      </td>
                    )}
                  </tr>
                ))
                )}
              </tbody>
            </table>
        </div>

        {companiesData.length !== 0 && (
          <ContactPagination
            from="company"
            selectedItem={selectedItem}
            paginationData={paginationData}
            handleNext={handleNext}
            handlePrev={handlePrev}
            tags={tagss}
            setTags={setTagss}
            onSuccess={(filters) => onSuccess(filters, advanceFilterData)}
            itemPerPage={itemPerPage}
            handleItemPerPageSelect={(value) => {
              setItemPerPage(value);
              setCurrentPage(1);
            }}
          />
        )}
        <div className="px-6 pb-2">
          <DataAsOfNotice  />
        </div>
      </div>

      {/* Column Customizer Modal */}
      {showColumnModal && (
        <ColumnCustomizer
          allColumns={allColumns}
          visibleColumns={visibleColumns}
          setVisibleColumns={setVisibleColumns}
          storageKey="companyVisibleColumns"
          onClose={() => setShowColumnModal(false)}
        />
      )}

      <AddCompanyModal
        showModal={showModal}
        onClose={() => setShowModal(false)}
        onCallApi={(filters) => onSuccess(filters, advanceFilterData)}
      />
    </BaseLayout>
  );
};

export default Companies;